import { memo } from 'react';
import {
  usePlayerStore,
  useExamSessionStore,
} from '../../../stores/StoreContext';
import VideoRenderer from '../VideoRenderer';
import NoTracksLive from '../NoTracksLive';
import NoTracksRecorded from '../NoTracksRecorded';
import NoVideo from '../NoVideo';

/**
 * SessionLayout - Video player rendering component for LiveKit sessions
 * 
 * This component handles the conditional rendering of video content based on track
 * availability and session state. It reads from Zustand stores to determine what
 * to display without directly managing LiveKit hooks.
 * 
 * **Performance Optimization:**
 * The component is wrapped with React.memo() to prevent unnecessary re-renders from
 * parent component updates. LiveKit hook monitoring (recording status and tracks) is
 * handled by separate monitor components (RecordingMonitor and TrackMonitor) to
 * prevent this component from re-rendering on audio activity changes.
 * 
 * **Architecture:**
 * - RecordingMonitor: Handles useIsRecording() hook and updates ExamSessionStore
 * - TrackMonitor: Handles useTracks() hook and updates PlayerStore
 * - SessionLayout: Pure rendering component based on store state
 * 
 * **Rendering Logic:**
 * - Shows VideoRenderer when tracks are available (hasTracks = true)
 * - Shows NoTracksLive when session is live but no tracks yet
 * - Shows NoTracksRecorded when session is recorded but no tracks available
 * - Shows NoVideo when no journey step is active
 * 
 * @component
 * @returns {JSX.Element} Video player or appropriate no-tracks message
 * 
 * @example
 * // Used within LiveKitRoom component alongside monitor components
 * <LiveKitRoom token={token} serverUrl={url}>
 *   <RecordingMonitor />
 *   <TrackMonitor />
 *   <SessionLayout />
 * </LiveKitRoom>
 * 
 * @see {@link RecordingMonitor} For recording status monitoring
 * @see {@link TrackMonitor} For track monitoring and management
 * @see {@link VideoRenderer} For video playback rendering
 */
function SessionLayoutComponent() {
  // Read state from stores - no LiveKit hooks here to prevent re-renders on audio activity
  const isLive = usePlayerStore((state) => state.isLive);
  const hasTracks = usePlayerStore((state) => state.hasTracks);
  const currentJourneyStepId = useExamSessionStore(
    (state) => state.journey.currentJourneyStepId
  );

  /**
   * Render appropriate message when no tracks are available
   * 
   * @returns {JSX.Element} Appropriate no-tracks component based on session state
   */
  const renderNoTracksMessage = () => {
    if (!currentJourneyStepId) {
      return <NoVideo />;
    }
    if (isLive) {
      return <NoTracksLive />;
    }
    return <NoTracksRecorded />;
  };

  return (
    <>
      {/* Video Player */}
      {hasTracks ? (
        <VideoRenderer />
      ) : (
        renderNoTracksMessage()
      )}
    </>
  );
}

/**
 * Memoized export to prevent unnecessary re-renders from parent component
 * 
 * The parent LiveKitRoom component may re-render frequently due to connection state
 * changes, participant updates, and other LiveKit events. Memoization ensures that
 * SessionLayout only re-renders when Zustand store state actually changes.
 * 
 * This is safe because:
 * - Component has no props to compare
 * - All state comes from Zustand hooks which trigger re-renders independently
 * - LiveKit hooks are isolated in separate monitor components
 * - No risk of stale closures or missed updates
 * 
 * **Performance Impact:**
 * By removing LiveKit hooks from this component, it no longer re-renders on audio
 * activity changes (speaking, audio levels, etc.). Only re-renders when actual
 * video state changes (tracks added/removed, live status, journey step changes).
 */
const SessionLayout = memo(SessionLayoutComponent);

export { SessionLayout };
export default SessionLayout;
